import React, { useCallback } from 'react';
import classnames from 'classnames';
import { useSearchParams } from 'react-router-dom';

import { debounce } from 'buyside/util/lodashAlt';

import styles from './filterTextInputStyles.scss';

const FilterTextInput: React.FC<{
  analyticsTag?: any;
  defaultValue?: string;
  disabled?: boolean;
  id: string;
  isDarkTheme?: boolean;
  paramName: string;
  placeholder?: string;
  text?: string;
}> = ({ text, placeholder, id, analyticsTag, isDarkTheme, disabled, paramName }) => {
  const [searchParams, setSearchParams] = useSearchParams();

  const currentParamValue = searchParams.get(paramName) || '';

  const setParamValue = useCallback(
    (paramValue: string) => {
      searchParams.set('page', '1');
      if (paramValue) {
        searchParams.set(paramName, paramValue);
      } else {
        searchParams.delete(paramName);
      }
      searchParams.delete('recommended');
      searchParams.delete('fpid');
      setSearchParams(searchParams);
    },
    [paramName, searchParams, setSearchParams]
  );

  const inputRef = React.useRef<HTMLInputElement | null>(null);

  const handleInsert = debounce(({ target: { value } }): void => setParamValue(value), 1500);

  const isDisabled = disabled !== undefined ? disabled : false;

  React.useEffect(() => {
    if (inputRef && !currentParamValue && inputRef.current) {
      inputRef.current.value = '';
    }
  }, [currentParamValue]);

  return (
    <div
      className={classnames(styles.container, {
        [styles.disabled]: isDisabled,
        [styles.darkTheme]: isDarkTheme
      })}
    >
      {text && <label htmlFor={id}>{text}</label>}

      <input
        id={id}
        type="text"
        data-testid="filter-text-input"
        ref={inputRef}
        defaultValue={currentParamValue}
        onChange={handleInsert}
        placeholder={placeholder || 'Type a value to search...'}
        data-heap={analyticsTag || id}
        disabled={isDisabled}
      />
    </div>
  );
};

export default FilterTextInput;
